import {
  type Color,
  type Dir,
  flairOf,
  ICE_SIZE,
  type Level,
  monsterPos,
  type Pos,
  type RunState,
} from '@xox/shared'
import { useEffect, useRef, useState } from 'react'
import { FALL_MS, type Facing, facingAfter, glideMs, slideDir, YETI_STEP_MS } from './anim'
import { Skater } from './art/Skater'
import { TileLayer } from './art/TileLayer'
import { Yeti } from './art/Yeti'

export interface Puck {
  id: string
  pos: Pos
  color?: Color | null
  /** Other racers render translucent so your own puck stays readable. */
  ghost?: boolean
  label?: string
  /** Mid-fall: shown at the hole, shrinking; the parent snaps it back to the start afterwards. */
  falling?: boolean
  finished?: boolean
}

interface Props {
  level: Level
  pucks: Puck[]
  /** The viewer's own run: drives flicker phase, cracked tiles, key state and yeti positions. */
  run?: RunState | null
  doorReached?: boolean
  className?: string
}

const CELL = 100 / ICE_SIZE

interface PuckMotion {
  puck: Puck
  /** Cells travelled since the last committed position (0 when idle). */
  cells: number
  dir: Dir | null
  facing: Facing
  /** Glide duration to keep on the element, so mid-glide re-renders don't restart it. */
  dur: number
}

export function IceBoard({ level, pucks, run, doorReached = false, className = '' }: Props) {
  const motion = usePuckMotion(pucks)
  const leaning = usePuckLean(motion)
  const t = run?.moves ?? 0
  const monsters = level.monsters ?? []

  return (
    <div
      className={`relative aspect-square select-none overflow-hidden rounded-3xl bg-slate-300 shadow-inner ring-1 ring-slate-300 dark:bg-slate-700 dark:ring-slate-700 ${className}`}
      style={{ width: 'min(92vw, 60dvh, 400px)' }}
      role="img"
      aria-label={describeBoard(level, pucks)}
    >
      <TileLayer level={level} run={run} doorReached={doorReached} />

      {monsters.map((m, i) => {
        const p = monsterPos(m, t)
        const next = monsterPos(m, t + 1)
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: yetis are identified by their index in the level
            key={`yeti-${i}`}
            className="pointer-events-none absolute flex items-end justify-center"
            style={{
              width: `${CELL}%`,
              height: `${CELL}%`,
              left: `${p.c * CELL}%`,
              top: `${p.r * CELL}%`,
              transition: `left ${YETI_STEP_MS}ms ease-out, top ${YETI_STEP_MS}ms ease-out`,
              zIndex: 3,
            }}
            aria-hidden
          >
            <Yeti facing={next.c < p.c ? 'left' : 'right'} className="w-[92%]" />
          </div>
        )
      })}

      {motion.map(({ puck: p, facing, dur }) => (
        <div
          key={p.id}
          className="pointer-events-none absolute flex flex-col items-center justify-end"
          style={{
            width: `${CELL}%`,
            height: `${CELL}%`,
            left: `${p.pos.c * CELL}%`,
            top: `${p.pos.r * CELL}%`,
            transition: `left ${dur}ms cubic-bezier(.2,.8,.2,1), top ${dur}ms cubic-bezier(.2,.8,.2,1)`,
            zIndex: p.ghost ? 1 : 2,
          }}
        >
          <div
            className={[
              'flex w-full items-end justify-center transition-all',
              p.ghost ? 'opacity-45 saturate-50' : 'drop-shadow-md',
              p.falling ? 'scale-25 opacity-0 rotate-[30deg]' : '',
              p.finished ? 'animate-bounce' : '',
            ].join(' ')}
            style={{ transitionDuration: `${FALL_MS}ms` }}
          >
            <Skater
              color={flairOf({ id: p.id, color: p.color }).color}
              facing={facing}
              lean={leaning[p.id] ?? null}
              mood={p.falling ? 'falling' : p.finished ? 'finished' : 'idle'}
              className="w-[64%]"
            />
          </div>
          {p.label && p.ghost && (
            <span className="absolute -bottom-2 max-w-[150%] truncate rounded-full bg-slate-900/70 px-1 text-[8px] font-semibold leading-3 text-white">
              {p.label}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Compares each puck with its last committed position. Render stays pure (StrictMode renders
 * twice); the refs are updated in an effect after the commit.
 */
function usePuckMotion(pucks: Puck[]): PuckMotion[] {
  const prevPos = useRef(new Map<string, Pos>())
  const facing = useRef(new Map<string, Facing>())
  const lastDur = useRef(new Map<string, number>())
  const motion = pucks.map((puck) => {
    const prev = prevPos.current.get(puck.id) ?? puck.pos
    const cells = Math.abs(prev.r - puck.pos.r) + Math.abs(prev.c - puck.pos.c)
    return {
      puck,
      cells,
      dir: slideDir(prev, puck.pos),
      facing: facingAfter(prev, puck.pos, facing.current.get(puck.id) ?? 'right'),
      dur: cells > 0 ? glideMs(cells) : (lastDur.current.get(puck.id) ?? glideMs(0)),
    }
  })
  useEffect(() => {
    for (const m of motion) {
      prevPos.current.set(m.puck.id, m.puck.pos)
      facing.current.set(m.puck.id, m.facing)
      if (m.cells > 0) lastDur.current.set(m.puck.id, m.dur)
    }
  })
  return motion
}

/** Which direction each puck is currently sliding in, cleared once its glide finishes. */
function usePuckLean(motion: PuckMotion[]) {
  const [leaning, setLeaning] = useState<Record<string, Dir | null>>({})
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const moves = motion.filter((m) => m.dir !== null)
  const key = moves.map((m) => `${m.puck.id}:${m.dir}:${m.cells}`).join('|')

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` summarises every move this render
  useEffect(() => {
    if (moves.length === 0) return
    setLeaning((s) => ({ ...s, ...Object.fromEntries(moves.map((m) => [m.puck.id, m.dir])) }))
    for (const m of moves) {
      clearTimeout(timers.current.get(m.puck.id))
      timers.current.set(
        m.puck.id,
        setTimeout(() => setLeaning((s) => ({ ...s, [m.puck.id]: null })), m.dur),
      )
    }
  }, [key])

  useEffect(() => {
    const t = timers.current
    return () => {
      for (const id of t.values()) clearTimeout(id)
    }
  }, [])

  return leaning
}

function describeBoard(level: Level, pucks: Puck[]): string {
  const holes = level.rows.join('').split('o').length - 1
  const yetis = level.monsters?.length ?? 0
  const you = pucks.find((p) => !p.ghost)
  const where = you ? `, you are on row ${you.pos.r} column ${you.pos.c}` : ''
  return `${level.name}: 10 by 10 ice board with ${holes} ${holes === 1 ? 'hole' : 'holes'}${yetis ? ` and ${yetis} ${yetis === 1 ? 'yeti' : 'yetis'}` : ''}${where}`
}
